import { ReactElement } from 'react';
import { Control, Controller, FieldValues } from 'react-hook-form';
import { Autocomplete, LinkButton, Typography } from '@weareopenr/component-library';
import { IPublishDestination } from '../types';
import { destinationId } from '../utilities/destinationId/destinationId';
import { getSelectAllIds } from '../utilities/getSelectAllIds/getSelectAllIds';
import styles from '../PublishSetupView/PublishSetupView.module.scss';

interface IChannelsFieldProps {
  control: Control<FieldValues>;
  publicationData: IPublishDestination[];
  /**
   * Given the current selection, why this destination cannot be picked — or undefined if it can.
   * Menu publish omits it and every destination stays selectable.
   */
  getDisabledReason?: (destination: IPublishDestination, selectedIds: string[]) => string | undefined;
}

const ChannelsField = ({ control, publicationData, getDisabledReason }: IChannelsFieldProps): ReactElement => {
  return (
    <div className={styles['section']}>
      <Typography weight="bold" size="medium">
        Channels
      </Typography>
      <Controller
        name="selectedChannels"
        control={control}
        render={({ field }) => {
          const selectedIds: string[] = field.value ?? [];
          const selectedChannels = new Set<string>(selectedIds);
          const options = publicationData.map((destination) => {
            const reason = getDisabledReason?.(destination, selectedIds);
            return {
              value: destinationId(destination),
              label: destination.name,
              ...(reason && {
                content: (
                  <span>
                    {destination.name} <em>({reason})</em>
                  </span>
                ),
              }),
              disabled: Boolean(reason),
            };
          });
          const selectableIds = getSelectAllIds(publicationData, selectedIds, getDisabledReason);
          const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedChannels.has(id));

          return (
            <>
              <div className={styles['autocomplete']}>
                <Autocomplete
                  multiple
                  name="selectedChannels"
                  label="Publish Channels"
                  options={options}
                  value={field.value ?? []}
                  onChange={(values) => field.onChange(values)}
                  limitChips={4}
                />
              </div>
              <div className={styles['select-all-controls']}>
                <LinkButton type="button" onClick={() => field.onChange(allSelected ? [] : selectableIds)}>
                  {`${allSelected ? 'Deselect' : 'Select'} all (${selectableIds.length})`}
                </LinkButton>
              </div>
            </>
          );
        }}
      />
    </div>
  );
};

export default ChannelsField;
